import { createSelector } from 'reselect'

// Base selectors
export const selectWorkLogContent = (state) => state.workLog?.workLog?.data?.content
export const selectTerminatedContent = (state) => state.workLog?.workLog?.data?.terminatedContent
export const selectWorkLogLoading = (state) => state.workLog?.workLog?.loading
export const selectWorkLogAssigned = (state) => state.workLog?.workLog?.data

// UI selectors
export const selectActiveWorkLogOrg = (state) => state.workLog?.ui?.activeWorkLogOrg
export const selectActiveTab = (state) => state.workLog?.ui?.activeTab
export const selectActiveTabEmployer = (state) => state.workLog?.ui?.activeTabEmployer
export const selectSelectedCarouselDate = (state) => state.workLog?.ui?.selectedCarouselDate
export const selectEmployeeSelectedCarouselDate = (state) => state.workLog?.ui?.employeeSelectedCarouselDate
export const selectDisabledDates = (state) => state.workLog?.ui?.disabledDates || []
export const selectEmployeeDisabledDates = (state) => state.workLog?.ui?.employeeDisabledDates || []
export const selectSelectedEmployee = (state) => state.workLog?.ui?.selectedEmployee
export const selectSearchQuery = (state) => state.workLog?.ui?.searchQuery || ''
export const selectNewlyAddedDates = (state) => state.workLog?.ui?.newlyAddedDates || []
export const selectErrorMessage = (state) => state.workLog?.ui?.errorMessage || ''
export const selectIsCompanyAllowed = (state) => state.workLog?.ui?.isCompanyAllowed

// Modal selectors
export const selectIsDownloadModalOpen = (state) => state.workLog?.ui?.modals?.isDownloadModalOpen || false
export const selectIsDeleteModalOpen = (state) => state.workLog?.ui?.modals?.isDeleteModalOpen || false
export const selectIsUpdateCompaniesModalOpen = (state) =>
	state.workLog?.ui?.modals?.isUpdateCompaniesModalOpen || false

// Date picker selectors
export const selectIsDatePickerOpen = (state) => state.workLog?.ui?.datePickers?.isDatePickerOpen || false
export const selectIsSearchDatePickerOpen = (state) => state.workLog?.ui?.datePickers?.isSearchDatePickerOpen || false
export const selectIsEmployerSearchDatePickerOpen = (state) =>
	state.workLog?.ui?.datePickers?.isEmployerSearchDatePickerOpen || false
export const selectSelectedDate = (state) => state.workLog?.ui?.datePickers?.selectedDate
export const selectSearchSelectedDate = (state) => state.workLog?.ui?.datePickers?.searchSelectedDate
export const selectEmployerSearchSelectedDate = (state) => state.workLog?.ui?.datePickers?.employerSearchSelectedDate

// Computed selectors
export const selectActiveOrgDepartments = createSelector(
	[selectWorkLogContent, selectActiveWorkLogOrg, selectWorkLogLoading],
	(workLogContent, activeWorkLogOrg, isLoading) => {
		if (
			isLoading ||
			!activeWorkLogOrg ||
			!workLogContent ||
			!Array.isArray(workLogContent) ||
			workLogContent.length === 0
		)
			return []

		const selectedOrg = workLogContent.find((org) => org.bin === activeWorkLogOrg)
		if (!selectedOrg?.employees?.length) return []

		const firstEmployee = selectedOrg.employees[0]
		if (!firstEmployee.depts) return []

		return firstEmployee.depts.map((dept) => ({
			code: dept.code,
			nameRu: dept.nameRu,
			nameKz: dept.nameKz,
		}))
	}
)

export const selectWorksForSelectedDateAndDept = createSelector(
	[selectWorkLogContent, selectActiveWorkLogOrg, selectSelectedCarouselDate, selectActiveTab, selectWorkLogLoading],
	(workLogContent, activeWorkLogOrg, selectedCarouselDate, activeTab, isLoading) => {
		if (
			isLoading ||
			!activeWorkLogOrg ||
			!selectedCarouselDate ||
			!activeTab ||
			!workLogContent ||
			!Array.isArray(workLogContent) ||
			workLogContent.length === 0
		)
			return []

		const selectedOrg = workLogContent.find((org) => org.bin === activeWorkLogOrg)
		if (!selectedOrg?.employees?.length) return []

		const firstEmployee = selectedOrg.employees[0]
		if (!firstEmployee.depts) return []

		const selectedDept = firstEmployee.depts.find((dept) => dept.code === activeTab)
		if (!selectedDept?.works) return []

		const formatDate = (date) => {
			const day = String(date.getDate()).padStart(2, '0')
			const month = String(date.getMonth() + 1).padStart(2, '0')
			const year = date.getFullYear()
			return `${day}.${month}.${year}`
		}

		const formattedDate = formatDate(selectedCarouselDate)

		return selectedDept.works.filter((work) => {
			if (!work.date) return false

			if (work.date.includes('.')) {
				return work.date === formattedDate
			} else {
				const workDate = new Date(work.date)
				if (isNaN(workDate.getTime())) return false
				return formatDate(workDate) === formattedDate
			}
		})
	}
)

export const selectUniqueEmployeesCount = createSelector(
	[
		(state) => state.workLog?.employer?.activeEmployeeList?.data,
		(state) => state.workLog?.employer?.firedEmployeeList?.data,
		selectActiveTabEmployer,
	],
	(activeList, firedList, activeTabEmployer) => {
		const source = activeTabEmployer === 'timingWork.dismissed' ? firedList : activeList
		return Array.isArray(source) ? source.length : 0
	}
)

export const selectIsWorksEmployee = createSelector(
	[(state) => state.workLog?.employer?.activeEmployeeList?.data],
	(activeEmployeeList) => Array.isArray(activeEmployeeList) && activeEmployeeList.length > 0
)
